/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useRef } from 'react';
import {
    string, func, shape, arrayOf, bool, array,
} from 'prop-types';
import { useQuery } from '@apollo/client';
import mbpLogger from '@module-logger/mbp';
import { useSelector } from 'react-redux';

// redux, hooks, helpers
import { GRAPHQL_ENV } from '../../../gql';
import FlashDealUI from './FlashDealUI';
import findSkuByPartNumbers from '../../../gql/queries/findSkuByPartNumbers';
import HeroSkeleton from '../GraphqlSkeletonComponents/HeroSkeleton';
import useClickStreamCustomEventsTracking from '../../../helpers/Personalization/useClickstreamCustomEventsTracking';
import { getBrand } from '../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import useExperience from '../../../helpers/Personalization/useExperience';

const isProductAvailable = (product) => product?.availability?.availabilityIndicator === 'true';

const isProductPublished = (product) => product?.availability?.published || false; // returning bool in gql

const getBrandId = (productCode) => {
    try {
        if (productCode) {
            if (productCode.substring(0, productCode.indexOf('-'))) {
                return productCode.substring(0, productCode.indexOf('-'));
            }

            return productCode.substring(0, productCode.indexOf('_'));
        }
    } catch (ex) {
        mbpLogger.logError({
            productCode,
            function: 'getBrand',
            appName: process.env.npm_package_name,
            module: 'tracking',
            jsError: ex,
            message: 'Error getting product brand.',
        });
    }

    return null;
};

const transformProducts = (products, brandName) => products.map((product) => { // restructuring salesforce data for AB test
    const {
        partNumber, name, image, seo, skuPriceRange, productType = 'REGULAR_PRODUCT',
    } = product;

    const prices = [
        ...(skuPriceRange.retail || []).map((price) => ({
            __typename: 'Price',
            currency: null,
            type: 'retail',
            value: price.value,
        })),
        ...(skuPriceRange.sale || []).map((price) => ({
            __typename: 'Price',
            currency: null,
            type: 'sale',
            value: price.value,
        })),
    ];

    const brandId = getBrandId(partNumber);

    return {
        __typename: 'ProductSku',
        brand: brandName,
        brandId,
        id: partNumber,
        partNumber,
        availability: {
            __typename: 'ProductAvailability',
            availabilityIndicator: 'true',
            published: true,
        },
        name,
        productType,
        prices,
        image,
        parentProduct: {
            __typename: 'ProductBase',
            seo,
            partNumber,
        },
    };
});

const FlashDealContentContainer = ({
    content, handleCloseTo, brand, heading, tracking, isCheckoutRecommendation, nbcuMovie, recipients, skuData,
}) => {
    const seedPartNumber = recipients?.[recipients?.length - 1]?.cartItems?.[0]?.product?.parentProductCode;
    const persistedPartNumber = useRef(seedPartNumber);
    // skuData of current selected add to cart product from PDP.
    const isSympathySku = skuData.some((sku) => sku.sympathyValue === '1' || sku.sympathyValue === '2');
    const { domain: brandName, id: brandId } = useSelector(getBrand);

    const {
        loading: recsLoading,
        error: recsError,
        data: recsData,
        variables: recsVars,
    } = useExperience({
        interactionName: 'harryanddavid-GetCampaign-MiniCartRecs',
        targeting: { pageType: 'product' },
        type: 'product',
        resolveIdentity: true,
        userAttributes: [
            { name: 'isSympathy', value: isSympathySku ? 'true' : 'false' },
            { name: 'referenceProductId', value: persistedPartNumber?.current },
        ],
        queryName: 'Personalized_mini_cart_recommendations',
    });

    const experimentTracking = recsData?.experience?.experimentTracking;
    const products = recsData?.experience?.products || [];

    const trackClickThrough = useClickStreamCustomEventsTracking({
        experimentTracking,
        isFireImpression: true,
        page: { type: 'product' },
    });

    if (recsError) {
        mbpLogger.logError({
            function: 'FlashDealContentContainer.js',
            message: 'Error loading data from vertex api',
            appName: process.env.npm_package_name,
            module: 'PersonalizedContent',
            jsError: recsError,
        });
    }

    const getPartNumbers = [];
    content.forEach((getSkuData) => {
        getPartNumbers.push(`"${getSkuData?.sku}"`);
    });

    const partNumbers = `[${getPartNumbers.join(',')}]`;

    const FLASH_DEAL_QUERY = findSkuByPartNumbers(
        brand.domain,
        partNumbers,
        `brand
        brandId
        id
        partNumber
        availability{
            availabilityIndicator
            published
        }
        name
        productType
        prices {
            currency
            type
            value
        }
        image {
            name
            path
        }
        parentProduct {
            seo {
                url
            }
            partNumber
        }`,
    );
    const { loading, error, data } = useQuery(FLASH_DEAL_QUERY);
    if (loading || recsLoading || recsVars?.skip) {
        return <HeroSkeleton height="200px" width="100%" />;
    }

    if (error) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            query: FLASH_DEAL_QUERY,
            jsError: error,
            component: 'FlashDealContentContainer.js',
            message: `Failed to fetch content data for: content type on brand ${brand}`,
            env: GRAPHQL_ENV,
        });
        handleCloseTo(false);
    }
    if (!data) {
        mbpLogger.logWarning({
            appName: process.env.npm_package_name,
            query: FLASH_DEAL_QUERY,
            component: 'FlashDealContentContainer.js',
            message: 'No data returned for query',
            env: GRAPHQL_ENV,
        });
        handleCloseTo(false);
        return null;
    }
    let getFlashData = data?.findSkuByPartNumbers?.filter((sku) => (isProductAvailable(sku) && isProductPublished(sku))) || [];

    const userGroup = experimentTracking?.name;
    if (products?.length > 0 && userGroup !== 'Control') {
        getFlashData = transformProducts(products, brandName);
    }

    return (
        <FlashDealUI isCheckoutRecommendation={isCheckoutRecommendation} tracking={tracking} productClickCallback={trackClickThrough} content={getFlashData} heading={heading} handleCloseTo={handleCloseTo} nbcuMovieData={nbcuMovie} />
    );
};

FlashDealContentContainer.propTypes = {
    content: arrayOf(shape({
        sku: string.isRequired,
    })),
    brand: shape({
        domain: string,
    }).isRequired,
    handleCloseTo: func,
    heading: string.isRequired,
    tracking: shape({
        eventCategory: '',
        eventAction: '',
    }),
    isCheckoutRecommendation: bool,
    nbcuMovie: arrayOf(arrayOf(shape({
        sku: string.isRequired,
        nbcu_see_titles: string.isRequired,
        info_on_hover: string.isRequired,
    }))),
    recipients: array,
    skuData: array,
};
FlashDealContentContainer.defaultProps = {
    content: [],
    handleCloseTo: () => { },
    tracking: {},
    isCheckoutRecommendation: false,
    nbcuMovie: [],
    recipients: [],
    skuData: [],
};

export default (FlashDealContentContainer);
